// frontend-driver/src/App.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { App as CapApp } from "@capacitor/app";
import i18next from "i18next";
import DriverWebApp from "./components/DriverWebApp.jsx";
import GuestDriverMarketplace from "./components/GuestDriverMarketplace.jsx"; // REISOR_STAGE134B2_GUEST_MARKET_V1
import Login from "./components/Login.jsx";
import Register from "./components/Register.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import ReisorProRoleGate from "./components/ReisorProRoleGate.jsx";
import ReisorProMarketAuth from "./components/ReisorProMarketAuth.jsx";
import DriverPushBootstrap from "./native/push/PushBootstrap.jsx";
import "./App.css";
import { consumeDriverInvitePromoFromUrl } from "./utils/driverReferralPromo";
import AppUpdateRequiredScreen from "./components/AppUpdateRequiredScreen.jsx";
import { fetchDriverAppVersionPolicy } from "./services/appVersionPolicy.js";
import {
  decorateDriverApiUrl,
  dispatchDriverAppUpdateRequired,
  DRIVER_APP_UPDATE_REQUIRED_EVENT,
} from "./services/appVersionTransport.js";
import {
  hasFreshPhoneVerificationReturnSignal,
  markPhoneVerificationReturnSignal,
} from "./utils/phoneVerificationReturnSignal.js";
import {
  readDriverPhoneVerificationState,
} from "./utils/driverPhoneVerificationState.js";

// === Telegram helpers (не режем функционал) ===
function tgWebApp() {
  try {
    return window?.Telegram?.WebApp || null;
  } catch {
    return null;
  }
}
function tgCloud() {
  const w = tgWebApp();
  return w?.CloudStorage || null;
}
function cloudGet(key) {
  const cloud = tgCloud();
  if (!cloud) return Promise.resolve("");
  return new Promise((resolve) => {
    try {
      cloud.getItem(String(key), (err, val) => {
        if (err) return resolve("");
        resolve(String(val || ""));
      });
    } catch {
      resolve("");
    }
  });
}
function cloudSet(key, value) {
  const cloud = tgCloud();
  if (!cloud) return Promise.resolve(false);
  return new Promise((resolve) => {
    try {
      cloud.setItem(String(key), String(value || ""), (err) => {
        resolve(!err);
      });
    } catch {
      resolve(false);
    }
  });
}

const REMOTE_API_ORIGIN = String(
  import.meta.env.VITE_BACKEND_ORIGIN || "https://backend.cargo-platform.online"
).replace(/\/+$/, "");

function isNativeLocalRuntime() {
  try {
    const protocol = String(window.location.protocol || "").toLowerCase();
    const host = String(window.location.hostname || "").toLowerCase();
    return (
      protocol === "capacitor:" ||
      protocol === "file:" ||
      host === "localhost" ||
      host === "127.0.0.1"
    );
  } catch {
    return false;
  }
}

function shouldUseAbsoluteBackendApi() {
  try {
    const origin = String(window?.location?.origin || "").trim();
    if (!origin) return false;

    // Only Capacitor/localhost runtime should hit backend origin directly.
    if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin)) return true;

    return false;
  } catch {
    return false;
  }
}

function absolutizeApiUrl(url) {
  try {
    if (typeof url !== "string") return url;
    if (!/^\/api(\/|$)/.test(url)) return url;

    if (!shouldUseAbsoluteBackendApi()) {
      return url;
    }

    return `https://backend.cargo-platform.online${url}`;
  } catch {
    return url;
  }
}

// === local token helpers ===
function readDriverTokenSync() {
  try {
    const v = localStorage.getItem("driver_token");
    if (v) return String(v);
  } catch {}
  try {
    const v = sessionStorage.getItem("driver_token");
    if (v) return String(v);
  } catch {}
  return "";
}
function shouldResumeDriverPhoneRegistration() {
  try {
    if (readDriverTokenSync()) return false;
    const driverState = readDriverPhoneVerificationState();
    if (!driverState?.verificationToken) return false;

    // A Market account uses the same Telegram return URL. Prefer its own resume
    // flow whenever an active CARGO/EXPEDITOR role+verification draft exists.
    const marketRole = String(localStorage.getItem("reisor_pro_entry_role") || "").toUpperCase();
    if (marketRole === "CARGO" || marketRole === "EXPEDITOR") {
      const marketState = localStorage.getItem(`reisor_market_phone_verify_v1:${marketRole}`);
      if (marketState) return false;
    }

    return true;
  } catch {
    return false;
  }
}

function persistDriverTokenSync(token) {
  const t = String(token || "").trim();
  if (!t) return;
  try {
    localStorage.setItem("driver_token", t);
  } catch {}
  try {
    sessionStorage.setItem("driver_token", t);
  } catch {}
  // cookie теперь ставит БЭК (HttpOnly). JS cookie не трогаем.
}


function persistDriverSnapshotSync(sessionOrDriver) {
  try {
    const d = sessionOrDriver?.driver || sessionOrDriver?.driverData || sessionOrDriver?.profile || sessionOrDriver || null;
    if (!d) return;
    const id = String(d?._id || d?.id || d?.driverId || "").trim();
    if (id) {
      try { localStorage.setItem("driver_id", id); } catch {}
      try { sessionStorage.setItem("driver_id", id); } catch {}
    }
    try {
      const snap = { ...(d || {}), _id: id || d?._id, id: id || d?.id, driverId: id || d?.driverId };
      localStorage.setItem("driver_data", JSON.stringify(snap));
    } catch {}
  } catch {}
}

// === api: session check ===
async function fetchDriverSession({ token } = {}) {
  const headers = {};
  const t = String(token || "").trim();
  if (t) headers.Authorization = /^bearer\s+/i.test(t) ? t : `Bearer ${t}`;

  const res = await fetch("/api/drivers/session", {
    method: "GET",
    headers,
    credentials: "include",
    cache: "no-store",
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP_${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}


// === park join (driver enters expeditor park by invite) ===
async function joinParkByInvite({ token, code, parkToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  const t = String(token || "").trim();
  if (t) headers.Authorization = /^bearer\s+/i.test(t) ? t : `Bearer ${t}`;

  const res = await fetch("/api/driver/park/join", {
    method: "POST",
    headers,
    credentials: "include",
    cache: "no-store",
    body: JSON.stringify({
      code: String(code || "").trim() || undefined,
      token: String(parkToken || "").trim() || undefined,
    }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP_${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function extractParkInviteFromUrl() {
  try {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("parkCode") || url.searchParams.get("park_code") || url.searchParams.get("joinCode") || "";
    const parkToken = url.searchParams.get("parkToken") || url.searchParams.get("park_token") || "";
    if (!code && !parkToken) return null;
    return { code, parkToken };
  } catch {
    return null;
  }
}

function removeParkInviteParamsFromUrl() {
  try {
    const url = new URL(window.location.href);
    ["parkCode", "park_code", "joinCode", "parkToken", "park_token"].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState({}, "", url.toString());
  } catch {}
}


function useViewportInsets() {
  useEffect(() => {
    const updateInsets = () => {
      try {
        const vv = window.visualViewport;
        if (!vv) {
          document.documentElement.style.setProperty("--vv-bottom", "0px");
          return;
        }

        const bottomInset = Math.max(
          0,
          window.innerHeight - vv.height - vv.offsetTop
        );

        document.documentElement.style.setProperty(
          "--vv-bottom",
          `${Math.round(bottomInset)}px`
        );
      } catch {
        document.documentElement.style.setProperty("--vv-bottom", "0px");
      }
    };

    updateInsets();

    window.addEventListener("resize", updateInsets);
    window.visualViewport?.addEventListener("resize", updateInsets);
    window.visualViewport?.addEventListener("scroll", updateInsets);

    return () => {
      window.removeEventListener("resize", updateInsets);
      window.visualViewport?.removeEventListener("resize", updateInsets);
      window.visualViewport?.removeEventListener("scroll", updateInsets);
    };
  }, []);
}


// REISOR_MATCH_STAGE51_MARKET_FREIGHT_DEEP_LINK_V1
function storeDeepMarketFreightIdFromAnyUrl(urlish, { cleanCurrentUrl = false, emit = true } = {}) {
  try {
    const raw = String(urlish || "").trim();
    if (!raw) return "";

    const url = new URL(raw, window.location.origin);
    const fid = String(
      url.searchParams.get("marketFreightId") ||
      url.searchParams.get("freightId") ||
      ""
    ).trim();

    if (!fid) return "";

    localStorage.setItem("deep_market_freight_id", fid);

    if (emit && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("reisor_driver_open_market_freight", {
          detail: { freightId: fid },
        })
      );
    }

    if (cleanCurrentUrl) {
      try {
        const cur = new URL(window.location.href);
        ["marketFreightId", "freightId"].forEach((k) => cur.searchParams.delete(k));
        window.history.replaceState({}, "", cur.pathname + cur.search + cur.hash);
      } catch {}
    }

    return fid;
  } catch {
    return "";
  }
}

// REISOR_DRIVER_APP_LINK_ORDER_V1
function storeDeepOrderIdFromAnyUrl(urlish, { cleanCurrentUrl = false, emit = true } = {}) {
  try {
    const raw = String(urlish || "").trim();
    if (!raw) return "";

    const url = new URL(raw, window.location.origin);
    const oid = String(
      url.searchParams.get("orderId") ||
      url.searchParams.get("order_id") ||
      url.searchParams.get("id") ||
      ""
    ).trim();

    if (!oid) return "";

    localStorage.setItem("deep_order_id", oid);

    if (emit && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("reisor_driver_open_order", { detail: { orderId: oid } })
      );
    }

    if (cleanCurrentUrl) {
      try {
        const cur = new URL(window.location.href);
        ["orderId", "order_id", "id"].forEach((k) => cur.searchParams.delete(k));
        window.history.replaceState({}, "", cur.pathname + cur.search + cur.hash);
      } catch {}
    }

    return oid;
  } catch {
    return "";
  }
}

export default function App() {
  useViewportInsets();
  const [token, setToken] = useState(undefined); // undefined=loading, null=unauth, string=auth
  const [driver, setDriver] = useState(null);

  // REISOR_ANDROID_1_0_87_MANDATORY_UPDATE_POLICY_CLIENT_V1
  const [
    appVersionPolicy,
    setAppVersionPolicy,
  ] = useState(null);

  const [
    appVersionPolicyChecking,
    setAppVersionPolicyChecking,
  ] = useState(false);

  const appVersionPolicyInFlightRef =
    useRef(null);

  const refreshAppVersionPolicy =
    useCallback(async () => {
      if (
        appVersionPolicyInFlightRef.current
      ) {
        return appVersionPolicyInFlightRef.current;
      }

      const run =
        (async () => {
          setAppVersionPolicyChecking(
            true
          );

          try {
            const policy =
              await fetchDriverAppVersionPolicy();

            setAppVersionPolicy(
              policy
            );

            return policy;
          } catch (error) {
            // Foundation rollout is fail-open.
            // Actual server HTTP 426 enforcement
            // is activated only after 1.0.87 is live.
            console.warn(
              "[driver-app-version-policy]",
              error?.message ||
                error?.name ||
                "CHECK_FAILED"
            );

            return null;
          } finally {
            setAppVersionPolicyChecking(
              false
            );
          }
        })();

      appVersionPolicyInFlightRef.current =
        run;

      try {
        return await run;
      } finally {
        if (
          appVersionPolicyInFlightRef.current ===
          run
        ) {
          appVersionPolicyInFlightRef.current =
            null;
        }
      }
    }, []);

  // Read-only version policy check.
  // Recheck after returning from Google Play.
  useEffect(() => {
    let disposed = false;

    const check = () => {
      if (!disposed) {
        refreshAppVersionPolicy();
      }
    };

    const onFocus = () => {
      check();
    };

    const onVisibility =
      () => {
        if (
          !document.hidden
        ) {
          check();
        }
      };

    check();

    window.addEventListener(
      "focus",
      onFocus
    );

    document.addEventListener(
      "visibilitychange",
      onVisibility
    );

    return () => {
      disposed = true;

      window.removeEventListener(
        "focus",
        onFocus
      );

      document.removeEventListener(
        "visibilitychange",
        onVisibility
      );
    };
  }, [refreshAppVersionPolicy]);

  // REISOR_ANDROID_1_0_87_GLOBAL_426_HANDLER_V1
  useEffect(() => {
    const onUpdateRequired =
      (event) => {
        const incomingPolicy =
          event?.detail?.policy;

        if (
          incomingPolicy &&
          typeof incomingPolicy === "object"
        ) {
          setAppVersionPolicy(
            (previous) => ({
              ...(previous || {}),
              ...incomingPolicy,
              updateRequired: true,
            })
          );

          return;
        }

        refreshAppVersionPolicy();
      };

    window.addEventListener(
      DRIVER_APP_UPDATE_REQUIRED_EVENT,
      onUpdateRequired
    );

    return () => {
      window.removeEventListener(
        DRIVER_APP_UPDATE_REQUIRED_EVENT,
        onUpdateRequired
      );
    };
  }, [refreshAppVersionPolicy]);

  // AUTO_JOIN_PARK_INVITE: if URL contains ?parkCode=... or ?parkToken=..., attempt to join once after auth.
  useEffect(() => {
    const inv = extractParkInviteFromUrl();
    if (!inv) return;
    if (!token || token === undefined) return; // need auth
    const t = String(token || "").trim();
    if (!t) return;

    (async () => {
      try {
        const r = await joinParkByInvite({ token: t, code: inv.code, parkToken: inv.parkToken });
        removeParkInviteParamsFromUrl();
        // eslint-disable-next-line no-alert
        alert(i18next.t("appAlerts.joinedPark"));
        console.log("park join ok:", r);
      } catch (e) {
        removeParkInviteParamsFromUrl();
        // eslint-disable-next-line no-alert
        alert(i18next.t("appAlerts.joinParkFailed", { error: e?.message || i18next.t("appAlerts.unknownError") }));
        console.warn("park join error:", e);
      }
    })();
  }, [token]);

  // guest | login | register_profile
  const [mode, setMode] = useState(() => {
    // REISOR_DRIVER_TELEGRAM_PHONE_VERIFICATION_V1
    // A cold Android return from Telegram must reopen driver registration instead
    // of flashing the guest role gate. CARGO/EXPEDITOR keep their own resume flow.
    try {
      if (
        hasFreshPhoneVerificationReturnSignal() &&
        shouldResumeDriverPhoneRegistration()
      ) {
        return "register_profile";
      }
    } catch {}
    return "guest";
  });

  // REISOR_PRO_ROLE_GATE_V1
  // Empty means show role selector for an unauthenticated neutral entry.
  // Driver-specific deep links bypass the selector to preserve existing flow.
  const [entryRole, setEntryRole] = useState(() => { try { const saved = String(localStorage.getItem("reisor_pro_entry_role") || "").toUpperCase(); return saved === "CARGO" || saved === "EXPEDITOR" ? saved : ""; } catch { return ""; } });

  useEffect(() => {
    try {
      const sp =
        new URLSearchParams(
          window.location.search || ""
        );

      const marketExpeditorFreightId = String(sp.get("marketExpeditorFreightId") || "").trim();
      if (marketExpeditorFreightId) {
        try {
          localStorage.setItem("reisor_pro_entry_role", "EXPEDITOR");
          localStorage.setItem("reisor_pro_market_expeditor_deep_freight_id", marketExpeditorFreightId);
        } catch {}
        setEntryRole("EXPEDITOR");
        try {
          sp.delete("marketExpeditorFreightId");
          const next = `${window.location.pathname}${sp.toString() ? `?${sp.toString()}` : ""}${window.location.hash || ""}`;
          window.history.replaceState({}, "", next);
        } catch {}
        return;
      }

      const driverIntent =
        sp.get("register") === "1" ||
        !!sp.get("marketFreightId") ||
        !!sp.get("freightId") ||
        !!sp.get("orderId") ||
        !!sp.get("order_id") ||
        !!sp.get("id") ||
        !!sp.get("parkCode") ||
        !!sp.get("park_code") ||
        !!sp.get("joinCode") ||
        !!sp.get("parkToken") ||
        !!sp.get("park_token");

      if (driverIntent) { try { localStorage.removeItem("reisor_pro_entry_role"); } catch {} setEntryRole("DRIVER"); }
    } catch {}
  }, []);


  useEffect(() => {
    consumeDriverInvitePromoFromUrl();
  }, []);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      const wantsRegister =
        sp.get("register") === "1";

      if (wantsRegister && !readDriverTokenSync()) {
        setMode("register_profile");
      }
    } catch {}
  }, []);

  useEffect(() => {
    const nativeRuntime =
      isNativeLocalRuntime();

    const originalFetch =
      window.fetch.bind(window);

    window.fetch =
      async (input, init) => {
        let nextInput = input;

        // URL transformation must never cause a second request.
        // If transformation fails, send the original request once.
        try {
          if (
            typeof input === "string"
          ) {
            const versionedUrl =
              decorateDriverApiUrl(
                input
              );

            nextInput =
              nativeRuntime
                ? absolutizeApiUrl(
                    versionedUrl
                  )
                : versionedUrl;
          } else if (
            input instanceof Request
          ) {
            const versionedUrl =
              decorateDriverApiUrl(
                input.url
              );

            const nextUrl =
              nativeRuntime
                ? absolutizeApiUrl(
                    versionedUrl
                  )
                : versionedUrl;

            if (
              nextUrl !== input.url
            ) {
              nextInput =
                new Request(
                  nextUrl,
                  input
                );
            }
          }
        } catch {}

        const response =
          await originalFetch(
            nextInput,
            init
          );

        try {
          await dispatchDriverAppUpdateRequired(
            response
          );
        } catch {}

        return response;
      };

    return () => {
      window.fetch =
        originalFetch;
    };
  }, []);

  useEffect(() => {
    const forceLogin = async () => {
      try { localStorage.removeItem("driver_token"); } catch {}
      try { sessionStorage.removeItem("driver_token"); } catch {}
      try { localStorage.removeItem("driver_id"); } catch {}
      try { sessionStorage.removeItem("driver_id"); } catch {}
      try { localStorage.removeItem("driver_data"); } catch {}
      try { sessionStorage.removeItem("driver_data"); } catch {}
      try { await cloudSet("driver_token", ""); } catch {}

      try {
        await fetch("/api/drivers/logout", { method: "POST", credentials: "include" });
      } catch {}

      setDriver(null);
      setToken(null);
      setMode("login");
    };

    window.addEventListener("gptcargo_driver_force_login", forceLogin);
    return () => window.removeEventListener("gptcargo_driver_force_login", forceLogin);
  }, []);

  // backend readiness (как было)
  const [backendReady, setBackendReady] = useState(null);

  // предотвращаем параллельные restore
  const restoreInFlightRef = useRef(null);

  // Deep links from Telegram / Android App Links.
  // REISOR orders use ?orderId=..., HUB freight uses ?marketFreightId=...
  // Telegram phone verification returns through the existing verified HTTPS App Link.
  useEffect(() => {
    let dead = false;
    let handle = null;

    const consumePhoneVerificationReturn = (
      urlish,
      { cleanCurrentUrl = false } = {}
    ) => {
      let parsed = null;

      try {
        parsed = new URL(String(urlish || ""), window.location.origin);
      } catch {
        return false;
      }

      if (
        String(parsed.searchParams.get("reisorPhoneVerification") || "").toLowerCase() !==
        "done"
      ) {
        return false;
      }

      // Durable marker closes Android cold-start race before the DOM event.
      markPhoneVerificationReturnSignal();

      // On an Android cold start Register is not mounted yet, so the DOM event
      // alone is insufficient. Reopen DRIVER registration only when its own
      // verification state is the active one; Market roles keep their flow.
      if (shouldResumeDriverPhoneRegistration()) {
        setMode("register_profile");
      }

      if (cleanCurrentUrl) {
        try {
          const current = new URL(window.location.href);
          current.searchParams.delete("reisorPhoneVerification");

          window.history.replaceState(
            window.history.state,
            "",
            `${current.pathname}${current.search}${current.hash}`
          );
        } catch {}
      }

      try {
        window.dispatchEvent(
          new CustomEvent("phone-verification-return", {
            detail: { status: "done" },
          })
        );
      } catch {}

      return true;
    };

    // Preserve the existing initial freight/order behavior: store without emit.
    const currentPhoneVerificationReturn =
      consumePhoneVerificationReturn(window.location.href, {
        cleanCurrentUrl: true,
      });

    if (!currentPhoneVerificationReturn) {
      const currentMarketFreight = storeDeepMarketFreightIdFromAnyUrl(
        window.location.href,
        { cleanCurrentUrl: true, emit: false }
      );

      if (!currentMarketFreight) {
        storeDeepOrderIdFromAnyUrl(window.location.href, {
          cleanCurrentUrl: true,
          emit: false,
        });
      }
    }

    const dispatchUrl = (urlish) => {
      if (consumePhoneVerificationReturn(urlish)) {
        return;
      }

      const marketFreightId =
        storeDeepMarketFreightIdFromAnyUrl(urlish, { emit: true });

      if (!marketFreightId) {
        storeDeepOrderIdFromAnyUrl(urlish, { emit: true });
      }
    };

    (async () => {
      try {
        const launch = await CapApp.getLaunchUrl();
        if (!dead && launch?.url) {
          dispatchUrl(launch.url);
        }
      } catch {}

      try {
        handle = await CapApp.addListener("appUrlOpen", (event) => {
          const url = String(event?.url || "").trim();
          if (url) dispatchUrl(url);
        });
      } catch {}
    })();

    return () => {
      dead = true;
      try { handle?.remove?.(); } catch {}
    };
  }, []);

  // backend status check (CANON HOTFIX)
// endpoint /api/monitor/status may be absent on prod → do not block UI
useEffect(() => {
  setBackendReady(true);
}, []);
// 🔥 Новый restore: сначала cookie-session, потом local/cloud как fallback и восстановление cookie
  useEffect(() => {
    let cancelled = false;

    // Try to make storage persistent (helps on Android/WebViews)
    (async () => {
      try {
        if (navigator?.storage?.persist) await navigator.storage.persist();
      } catch {}
    })();

    const restoreAuth = async ({ soft = false } = {}) => {
      if (restoreInFlightRef.current) return restoreInFlightRef.current;

      const p = (async () => {
        // 1) Пытаемся с cookie (серверная сессия)
        try {
          const s = await fetchDriverSession();
          const t = String(s?.token || "").trim();
          if (t) {
            persistDriverTokenSync(t);
            await cloudSet("driver_token", t);
          }
          persistDriverSnapshotSync(s);
          if (!cancelled) {
            setDriver(s?.driver || null);
            setToken((prev) => (soft && prev ? prev : t || null));
          }
          return;
        } catch (e) {
          // ignore -> fallback
        }

        // 2) fallback: localStorage/sessionStorage
        const tLocal = readDriverTokenSync();
        if (tLocal) {
          try {
            const s = await fetchDriverSession({ token: tLocal });
            const t = String(s?.token || tLocal || "").trim();
            if (t) {
              persistDriverTokenSync(t);
              await cloudSet("driver_token", t);
            }
            persistDriverSnapshotSync(s);
            if (!cancelled) {
              setDriver(s?.driver || null);
              setToken((prev) => (soft && prev ? prev : t || null));
            }
            return;
          } catch (e) {
            // ignore -> next fallback
          }
        }

        // 3) fallback: Telegram CloudStorage (если это Telegram WebApp)
        const tCloud = String(await cloudGet("driver_token") || "").trim();
        if (tCloud) {
          try {
            const s = await fetchDriverSession({ token: tCloud });
            const t = String(s?.token || tCloud || "").trim();
            if (t) {
              persistDriverTokenSync(t);
              await cloudSet("driver_token", t);
            }
            persistDriverSnapshotSync(s);
            if (!cancelled) {
              setDriver(s?.driver || null);
              setToken((prev) => (soft && prev ? prev : t || null));
            }
            return;
          } catch (e) {
            // ignore
          }
        }

        // 4) если ничего нет — не “выкидываем” при soft-restore (после сворачивания),
        // а только когда это первый запуск
        if (!cancelled) {
          setToken((prev) => {
            if (soft && prev) return prev;
            return prev === undefined ? null : prev;
          });
          if (!soft) setDriver(null);
        }
      })();

      restoreInFlightRef.current = p.finally(() => {
        restoreInFlightRef.current = null;
      });

      return restoreInFlightRef.current;
    };

    // первый запуск — жесткий restore
    restoreAuth({ soft: false });

    const onVis = () => {
      if (!document.hidden) restoreAuth({ soft: true });
    };
    window.addEventListener("focus", () => restoreAuth({ soft: true }));
    window.addEventListener("pageshow", () => restoreAuth({ soft: true }));
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", () => restoreAuth({ soft: true }));
      window.removeEventListener("pageshow", () => restoreAuth({ soft: true }));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const handleLogin = async (jwt, driverData) => {
    const t = String(jwt || "").trim();
    if (t) {
      persistDriverTokenSync(t);
      await cloudSet("driver_token", t);
    }

    // ✅ сохраняем driver_id/driver_data (чинит DRIVER_ID_MISSING после восстановления сессии)
    persistDriverSnapshotSync(driverData || null);

    // Сразу подтверждаем/обновляем server-cookie сессию (самый важный момент)
    try {
      const s = await fetchDriverSession({ token: t });
      setDriver(s?.driver || driverData || null);
      setToken(String(s?.token || t || "").trim() || null);
      persistDriverSnapshotSync(s);
    } catch {
      // если по какой-то причине session не поднялась — оставим как минимум токен в памяти
      setDriver(driverData || null);
      setToken(t || null);
    }

    setMode("login");
  };

  const handleRegister = async () => {
    // ✅ принудительно открываем регистрацию (не профайл), даже если где-то "залип" токен/кука
    try { localStorage.removeItem("driver_token"); } catch {}
    try { sessionStorage.removeItem("driver_token"); } catch {}
    try { localStorage.removeItem("driver_id"); } catch {}
    try { sessionStorage.removeItem("driver_id"); } catch {}
    try { localStorage.removeItem("driver_data"); } catch {}
    try { sessionStorage.removeItem("driver_data"); } catch {}
    try { await cloudSet("driver_token", ""); } catch {}

    try {
      await fetch("/api/drivers/logout", { method: "POST", credentials: "include" });
    } catch {}

    setDriver(null);
    setToken(null);
    setMode("register_profile");
  };

  // Hard update UI is shown only when the
  // server explicitly returns updateRequired=true.
  if (
    appVersionPolicy?.updateRequired ===
    true
  ) {
    return (
      <AppUpdateRequiredScreen
        policy={appVersionPolicy}
        checking={
          appVersionPolicyChecking
        }
        onRetry={
          refreshAppVersionPolicy
        }
      />
    );
  }

  // Экран “подключение к серверу…” (как было)
  if (backendReady === null) {
    return (
      <div className="app-shell driver-app bg-gray-50 text-gray-800">
        <div className="text-center text-gray-500 mt-10">{i18next.t("appShell.connecting")}</div>
      </div>
    );
  }
  if (mode === "register_profile") {
    return (
      <div className="app-shell driver-app bg-gray-50 text-gray-800">
        <Register
          onCancel={() => setMode("guest")}
          onRegistered={(jwt, driverData) => handleLogin(jwt, driverData)}
        />
      </div>
    );
  }

  if (token === undefined) {
    return (
      <div className="app-shell driver-app bg-gray-50 text-gray-800 flex items-center justify-center">
        <div className="text-slate-500 text-sm">{i18next.t("appShell.restoring")}</div>
      </div>
    );
  }

  if (!token) {
    if (mode === "login") {
      return (
        <div className="app-shell driver-app bg-slate-950 text-gray-100">
          <div className="mx-auto w-full max-w-xl px-3 pt-3">
            <button
              type="button"
              onClick={() => setMode("guest")}
              className="mb-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-bold text-slate-200"
            >
              ← {i18next.t("guestMarket.backToMarket")}
            </button>
          </div>
          <Login onLogin={handleLogin} onRegister={handleRegister} />
        </div>
      );
    }

    if (!entryRole) {
      return (
        <ReisorProRoleGate
          onSelect={(role) => { const next = String(role || "").toUpperCase(); try { if (next === "CARGO" || next === "EXPEDITOR") localStorage.setItem("reisor_pro_entry_role", next); else localStorage.removeItem("reisor_pro_entry_role"); } catch {} setEntryRole(next); }}
        />
      );
    }

    if (
      entryRole === "CARGO" ||
      entryRole === "EXPEDITOR"
    ) {
      return (
        <>
          {entryRole === "EXPEDITOR" ? (
            <DriverPushBootstrap enabled={true} />
          ) : null}

          <ReisorProMarketAuth
            role={entryRole}
            onBack={() => { try { localStorage.removeItem("reisor_pro_entry_role"); } catch {} setEntryRole(""); }}
          />
        </>
      );
    }

    return (
      <GuestDriverMarketplace
        onLogin={() => setMode("login")}
        onRegister={handleRegister}
      />
    );
  }

  // REISOR_PRIORITY_EXACT_FREIGHT_PENDING_DRIVER_V1
  // An authenticated but not-yet-approved driver must still be able to open
  // the exact public freight from a deep link. Contact access remains server-gated.
  let pendingDeepMarketFreightId = "";
  try {
    pendingDeepMarketFreightId = String(
      localStorage.getItem("deep_market_freight_id") || ""
    ).trim();
  } catch {}

  const currentDriverStatus = String(driver?.status || "").trim().toLowerCase();

  if (pendingDeepMarketFreightId && currentDriverStatus !== "approved") {
    return (
      <GuestDriverMarketplace
        onLogin={() => setMode("login")}
        onRegister={handleRegister}
      />
    );
  }

  return (
    <ErrorBoundary>
      <DriverPushBootstrap enabled={true} />
      <DriverWebApp token={token} driver={driver} />
    </ErrorBoundary>
  );
}
